import type { CalloutMessages, SelectOption } from '@camelot/components'
import type { CheckPositionIsInBusinessAreaRequest, LocationCheckResult } from '~/api/location'
import type { GetLocationConfigRequest, PrebookingUiLocationConfig } from '~/api/prebooking'
import type { City } from '~/composables/city'
import type { Address } from '~/types/location'
import * as Sentry from '@sentry/vue'
import { useDebounceFn } from '@vueuse/core'
import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'
import { ExternalApi } from '~/api/location'
import { ExternalControllerApi } from '~/api/prebooking/apis/ExternalControllerApi'
import { usePrebookingTracking } from '~/composables/api/usePrebookingTracking'
import { BERLIN } from '~/composables/city'
import { useGoogleAPI } from '~/composables/google-api'
import { translate } from '~/composables/translate/translations'
// @ts-expect-error Say thanks Google Inc for such a beautiful code
import GeocoderRequest = google.maps.GeocoderRequest

import LatLng = google.maps.LatLng

const DEBOUNCING_INPUT_ADDRESS_VALIDATION_TIME_MS = 200
const locationAPI = new ExternalApi()
const prebookingApi = new ExternalControllerApi()

export const useLocationStore = defineStore('location', () => {
  const city = ref<City>(BERLIN)
  const address = ref<Address | undefined>(undefined)
  const addressOptions = reactive<Set<SelectOption>>(new Set<SelectOption>())
  const addressError = ref<CalloutMessages | undefined>(undefined)
  const prebookingLocationConfig = ref<PrebookingUiLocationConfig>()
  const lastTypedQuery = ref<string>('')

  const validateAddressInput = useDebounceFn(isValidAddress, DEBOUNCING_INPUT_ADDRESS_VALIDATION_TIME_MS)

  const { trackOutOfBusinessAreaAddressSearch } = usePrebookingTracking()
  const route = useRoute()

  watchEffect(async () => {
    try {
      // isValid can be null if the Promise is cancelled by the bouncer - for example if another request comes in
      const { geoCoderService } = useGoogleAPI()
      // the prebooking extended area does not apply to MCS subscriptions
      const acceptExtendedArea = !route.path?.includes('/subscription/')
      const isValid: boolean | undefined = await validateAddressInput(city.value, address.value, geoCoderService.value, acceptExtendedArea)
      addressError.value = isValid === false ? { error: translate('api_errors_nothomearea') } : undefined
      if (!isValid && address.value?.info) {
        await trackOutOfBusinessAreaAddressSearch(address.value, lastTypedQuery.value)
      }
    }
    catch (error) {
      Sentry.captureException(error)
      console.error('Error while checking address', error)
    }
  })

  const isValid = computed<boolean>(() => !!address.value && !addressError.value)

  async function getLocationConfig(): Promise<PrebookingUiLocationConfig> {
    const rq: GetLocationConfigRequest = { locationId: city.value.locationId }
    const result: PrebookingUiLocationConfig = await prebookingApi.getLocationConfig(rq)
    prebookingLocationConfig.value = { ...result }
    return result
  }
  return {
    city,
    address,
    addressOptions,
    addressError,
    isValid,
    lastTypedQuery,
    getLocationConfig,
    prebookingLocationConfig,
  }
})

async function isPositionInBusinessArea(request: CheckPositionIsInBusinessAreaRequest, acceptExtendedArea: boolean): Promise<boolean> {
  const result: LocationCheckResult = await locationAPI.checkPositionIsInBusinessArea(request)
  return !!result.inZone || (acceptExtendedArea && !!result.inPrebookingExtendedArea)
}

function isValidAddress(city: City, address: Address | undefined, geoCoderService: google.maps.Geocoder | undefined, acceptExtendedArea: boolean): Promise<boolean> {
  if (!address)
    return Promise.resolve(true)

  if (address.location) {
    return checkAirportLocation(address, city, acceptExtendedArea)
  }
  else if (geoCoderService) {
    return checkAddress(address, city, geoCoderService, acceptExtendedArea)
  }
  else {
    // "Normal" address but GeoCoderService not available
    return Promise.resolve(true)
  }
}

function checkAirportLocation(address: Address, city: City, acceptExtendedArea: boolean) {
  const re: CheckPositionIsInBusinessAreaRequest = {
    lcRequestRto: {
      position: { latitude: address.location!.latitude, longitude: address.location!.longitude },
      locationId: city.locationId,
    },
  }
  return isPositionInBusinessArea(re, acceptExtendedArea)
}

async function checkAddress(address: Address, city: City, geoCoderService: google.maps.Geocoder, acceptExtendedArea: boolean) {
  const request: GeocoderRequest = { address: address.info, componentRestrictions: { country: city.country } }
  const result = await geoCoderService.geocode(request)
  const location: LatLng = result.results[0].geometry.location

  const position = { latitude: location.lat(), longitude: location.lng() }

  if (!address.location)
    address.location = position

  const rq: CheckPositionIsInBusinessAreaRequest = { lcRequestRto: { position, locationId: city.locationId } }
  return isPositionInBusinessArea(rq, acceptExtendedArea)
}
